
import React, { useState } from "react";
import api from "./api";
import Login from "./Login";
import "./App.css";
import { Wallet, Droplet } from "lucide-react";
import ResetPassword from "./ResetPassword";
import Wiadomosci from "./Wiadomosci";

const icons = {
  wallet: Wallet,
  droplet: Droplet,
};

const menuItems = [
  { id: "dzialka", label: "Dane działki", icon: "⌂" },
  { id: "konto", label: "Dane konta", icon: "▤" },
  { id: "finanse", label: "Finanse", icon: "wallet" },
  { id: "woda", label: "Licznik wody", icon: "◉" },
  { id: "prad", label: "Licznik prądu", icon: "ϟ" },
];

const emptyChangeForm = {
  Imie: "",
  Nazwisko: "",
  Miasto: "",
  Kod: "",
  Ulica: "",
  Nr_dom: "",
  Nr_m: "",
  Tel_1: "",
  Email: "",
  opis: "",
};

const plotFields = [
  ["N_O_id", "Numer ogrodu"],
  ["N_D", "Numer działki"],
  ["Indeks", "Indeks działki"],
  ["Pow_d", "Powierzchnia działki"],
  ["Prad", "Prąd"],
  ["Rok_leg_prad", "Rok legalizacji licznika prądu"],
  ["OczekujePrad", "Licznik prądu oczekuje na wymianę"],
  ["Woda", "Woda"],
  ["Rok_leg_woda", "Rok legalizacji wodomierza"],
];

function formatPlotValue(key, value) {
  if (value === null || value === undefined || value === "") {
    return "Brak danych";
  }

  const normalized = String(value).trim().toUpperCase(); 
  if (key === "Prad" || key === "Woda") { 
    if (normalized === "T") return "TAK"; 
    if (normalized === "N") return "NIE"; 
  } 

  if (key === "OczekujePrad") { 
    if (normalized === "1") return "TAK"; 
    if (normalized === "0") return "NIE"; 
  }  

  if (key === "Pow_d") {
    return `${value} m²`;
  }

  return String(value);
}

function toRoman(value) {
  if (
    value === null ||
    value === undefined ||
    String(value).trim() === ""
  ) {
    return "Brak danych";
  }

  const number = Number(value);

  if (!Number.isInteger(number) || number < 1 || number > 3999) {
    return "Brak danych";
  }

  const romanValues = [
    [1000, "M"],
    [900, "CM"],
    [500, "D"],
    [400, "CD"],
    [100, "C"],
    [90, "XC"],
    [50, "L"],
    [40, "XL"],
    [10, "X"],
    [9, "IX"],
    [5, "V"],
    [4, "IV"],
    [1, "I"],
  ];

  let remaining = number;
  let result = "";

  for (const [arabic, roman] of romanValues) {
    while (remaining >= arabic) {
      result += roman;
      remaining -= arabic;
    }
  }

  return result;
}

function formatYesNo(value, key = "") {  
  if ( 
    value === null || 
    value === undefined || 
    String(value).trim() === "" 
  ) { 
    return "Brak danych"; 
  } 

  const normalized = String(value).trim().toUpperCase(); 
  if (key === "Prad" || key === "Woda") {
     if (normalized === "T") return "JEST"; 
     if (normalized === "N") return "NIE MA"; 
    } 
    if (key === "Czlonek") {
       if (normalized === "T") return "CZŁONEK PZD"; 
       if (normalized === "N") return "BRAK CZŁONKOSTWA"; 
      } 
    
  if (key === "OczekujePrad") { 
    if (normalized === "1") return "TAK"; 
    if (normalized === "0") return "NIE"; 
  } 
  return "Brak danych"; 
}

function isYes(value, key = "") { 
  if (value === null || value === undefined) {
    return false; 
  } 
  
  const normalized = String(value).trim().toUpperCase(); 
  if (key === "Prad" || key === "Woda" || key === "Czlonek") { 
    return normalized === "T"; 
  } 
  if (key === "OczekujePrad") { 
    return normalized === "1"; 
  }
  return normalized === "1" || normalized === "TAK"; 
}

/*-----*/

function App() {
  const [user, setUser] = useState(null);
  const [waterReadings, setWaterReadings] = useState([]);
  const [powerReadings, setPowerReadings] = useState([]);
  const [financeTab, setFinanceTab] = useState("podstawowe");
  const [investmentReadings, setInvestmentReadings] = useState([]);
  const [basicAccountReadings, setBasicAccountReadings] = useState([]);
  const [basicAccountBalance, setBasicAccountBalance] = useState(0);
  const [investmentBalance, setInvestmentBalance] = useState(0);
  const [dashboard, setDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState("dzialka");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [changeForm, setChangeForm] = useState({
    ...emptyChangeForm,
  });

  const [changeMessage, setChangeMessage] = useState("");
  const [changeError, setChangeError] = useState("");
  const [changeLoading, setChangeLoading] = useState(false);
  const [nrKontoPodstawowe, setNrKontoPodstawowe] = useState("");
  const [nrKontoInwestycyjne, setNrKontoInwestycyjne] = useState("");

  // Dane działki zwracane przez dashboard.php
  const plotData = dashboard?.dzialka ?? null;

  // Wysyłanie zgłoszenia zmiany danych do PHP
  const handleChangeSubmit = async (e) => {
    e.preventDefault();

    setChangeMessage("");
    setChangeError("");

    const payload = Object.fromEntries(
      Object.entries(changeForm).filter(
        ([, value]) => String(value ?? "").trim() !== ""
      )
    );
    
    payload.NrKlienta = plotData?.NrKlienta ?? "";
    payload.idU_szukane = user?.idU_szukane ?? "";
    payload.ZbazyNazwisko = user?.Nazwisko ?? "";
    payload.ZbazyImie = user?.Imie ?? "";

    console.log("Dane użytkownika do e-maila:", {
      nazwisko: payload.ZbazyNazwisko,
      imie: payload.ZbazyImie
    });

    if (Object.keys(payload).length === 0) {
      setChangeError("Wypełnij przynajmniej jedno pole.");
      return;
    }

    setChangeLoading(true);

    try {
      const response = await api.post("/change-account.php", payload);

      setChangeMessage(
        response.data?.message ||
          "Zgłoszenie zostało wysłane do administratora."
      );

      setChangeForm({ ...emptyChangeForm });
    } catch (err) {
      console.error("Błąd wysyłania zgłoszenia:", err);

      setChangeError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Nie udało się wysłać zgłoszenia. Spróbuj ponownie."
      );
    } finally {
      setChangeLoading(false);
    }
  };

  // Pobieranie danych panelu po zalogowaniu
  const handleLoginSuccess = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await api.get("/dashboard.php");
      console.log("Odpowiedź dashboard.php:", res.data);
      console.log("Numer konta podstawowego:", res.data.NrKontoPodstawowe);
      console.log("Numer konta inwestycyjnego:", res.data.NrKontoInwestycyjne);

      if (res.data?.user) {
        setUser(res.data.user);
        setWaterReadings(res.data.woda || []);
        setPowerReadings(res.data.prad || []);
        setInvestmentReadings(res.data.kontoInwestycyjne || []);
        setInvestmentBalance(
          Number(res.data.saldoInwestycyjne) || 0
        );
        setBasicAccountReadings(res.data.kontoPodstawowe || []);
        setBasicAccountBalance(
          Number(res.data.saldoKontoPodstawowe) || 0
        );
        setNrKontoPodstawowe(res.data.NrKontoPodstawowe ?? "");
        setNrKontoInwestycyjne(res.data.NrKontoInwestycyjne ?? "");

        // Cała odpowiedź zawiera również dane działki.
        setDashboard(res.data);
        setActiveTab("dzialka");
      } else {
        setError("Nie udało się pobrać danych użytkownika.");
      }
    } catch (err) {
      console.error("Błąd pobierania panelu:", err);

      setError(
        err.response?.data?.error ||
          "Nie udało się pobrać danych panelu."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post("/logout.php");
    } catch (err) {
      console.error("Błąd wylogowania:", err);
    }

    setUser(null);
    setDashboard(null);
    setWaterReadings([]);
    setPowerReadings([]);
    setInvestmentReadings([]);
    setBasicAccountReadings([]);
    setNrKontoPodstawowe("");
    setNrKontoInwestycyjne("");
    setInvestmentBalance(0);
    setBasicAccountBalance(0);
    setActiveTab("dzialka");
    setError("");
    setChangeMessage("");
    setChangeError("");
    setChangeForm({ ...emptyChangeForm });
  };


  if (!user) {
      const params = new URLSearchParams(window.location.search);
      const isResetPage =
          window.location.pathname.endsWith("/reset-password") ||
          window.location.pathname.endsWith("/reset-password/") ||
          window.location.pathname.endsWith("/reset-password.php");

      if (isResetPage) {
          return (
              <ResetPassword
                  onBack={() => {
                      window.history.replaceState(
                          {},
                          "",
                          window.location.pathname
                      );
                      window.location.reload();
                  }}
              />
          );
      }

      return (
          <>
              <Login onLoginSuccess={handleLoginSuccess} />
              {loading && (
                  <p className="app-message">
                      Pobieranie danych użytkownika…
                  </p>
              )}
              {error && <p className="app-error">{error}</p>}
          </>
      );
  }


  const fullName = [user.Imie, user.Nazwisko]
    .filter(Boolean)
    .join(" ");

  const accountName = user.Nazwa_1 || "Panel klienta";
  const activeMenuItem =
    menuItems.find((item) => item.id === activeTab) || menuItems[0];

  return (
    <div className="dashboard-app">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">
            <img src="/ipod.ico" alt="Logo" />
          </div>

          <div className="brand-text">
            <span className="brand-title">{accountName}</span>
            <span className="brand-caption">PANEL KLIENTA</span>
          </div>
        </div>

        <div className="menu-heading">MENU GŁÓWNE</div>

        <nav className="side-nav" aria-label="Menu panelu">
          {menuItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`nav-button ${
                activeTab === item.id ? "active" : ""
              }`}
              onClick={() => setActiveTab(item.id)}
              aria-current={
                activeTab === item.id ? "page" : undefined
              }
            >
              <span className="nav-icon" aria-hidden="true">
                {item.id === "finanse" ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect
                      x="3"
                      y="6"
                      width="18"
                      height="15"
                      rx="2"
                    />
                    <path d="M3 10h18" />
                    <path d="M16 14h.01" />
                    <path d="M7 6V4a2 2 0 0 1 2-2h8" />
                  </svg>
                ) : (
                  item.icon
                )}
              </span>

              <span>{item.label}</span>

              {activeTab === item.id && (
                <span className="nav-indicator" />
              )}
            </button>
          ))}
        </nav>

        <div className="sidebar-bottom">
          <div className="support-card">
            <div className="support-icon">?</div>
            <div>
              <strong>Potrzebujesz pomocy?</strong>
              <p>Skontaktuj się z administratorem.</p>
            </div>
          </div>
        </div>
      </aside>

      <main className="main-area">
        <header className="topbar">
          <div className="breadcrumb">
            <span>Panel klienta</span>
            <span className="breadcrumb-separator">/</span>
            <strong>{activeMenuItem.label}</strong>
          </div>

          <div className="topbar-actions">
            <div className="profile">
              <div className="profile-avatar">
                {user.Imie
                  ? user.Imie.charAt(0).toUpperCase()
                  : "U"}
              </div>

              <div className="profile-details">
                <strong>{fullName || "Użytkownik"}</strong>
                <span>Identyfikator {user.nr_user || "—"}</span>
              </div>
            </div>

            <button
              type="button"
              className="logout-button topbar-logout"
              onClick={handleLogout}
            >
              <span aria-hidden="true">↪</span>
              Wyloguj się
            </button>
          </div>
        </header>

        <section className="page-content">
          <div className="welcome-row">
            <div>
              <p className="eyebrow">STREFA KLIENTA</p>
              <h1>{activeMenuItem.label}</h1>
              <p className="page-subtitle">
                Witaj{user.Imie ? `, ${user.Imie}` : ""}. Tutaj
                znajdziesz informacje przypisane do Twojego konta.
              </p>
            </div>

            <div className="status-pill">
              <span className="status-dot" />
              Konto aktywne
            </div>
          </div>

          <div className="content-panel">             
            {/* DANE DZIAŁKI */}
            {activeTab === "dzialka" && (
              <div className="content-section">
                <div className="panel-heading">
                  <div>
                    <span className="panel-kicker">
                      INFORMACJE O DZIAŁCE
                    </span>
                    <h2>Dane działki</h2>
                  </div>
                  <span className="panel-symbol">⌂</span>
                </div>

                {!plotData ? (
                  <div className="empty-plot-message">
                    <span className="panel-symbol">⌂</span>
                    <h3>Brak danych działki</h3>
                    <p>
                      Nie otrzymano danych działki z serwera.
                      Sprawdź odpowiedź pliku dashboard.php.
                    </p>
                  </div>
                ) : (
                  <>
                     {/* Numer działki */} 
                      <div className="plot-number-card"> 
                        <span className="plot-label">Numer działki</span> 
                        <strong className="plot-number"> 
                          {toRoman(plotData.N_O_id)} 
                          {" \\"} 
                          {plotData.N_D != null && 
                          String(plotData.N_D).trim() !== "" 
                            ? plotData.N_D : "Brak danych"} 
                          {plotData.Indeks != null && 
                          String(plotData.Indeks).trim() !== "" 
                          ? ` ${String(plotData.Indeks).trim()}` 
                          : ""} 
                        </strong>                   
                      </div> 
                      
                      {/* Szczegółowe informacje */} 
                      <h3 className="plot-details-heading"> 
                        Szczegółowe informacje 
                      </h3> 
                          
                      {/* Powierzchnia działki */} 
                      <div className="plot-surface-card"> 
                        <span className="plot-label"> 
                          Powierzchnia działki 
                        </span> 
                        <strong className="plot-value"> 
                          {plotData.Pow_d != null && 
                          String(plotData.Pow_d).trim() !== "" 
                          ? `${plotData.Pow_d} m²` : "Brak danych"} 
                        </strong> 
                      </div> 
                      
                      {/* Prąd i woda */} 
                      <div className="plot-utilities-grid"> 

                        {/* Prąd */} 
                        <div className="plot-utility-card"> 
                          <h3>Prąd</h3> 
                          <strong className={`plot-utility-status ${ 
                            isYes(plotData.Prad) ? "is-yes" 
                            : formatYesNo(plotData.Prad, "Prad") === "NIE" 
                            ? "is-no" : "is-yes" 
                          }`} 
                        > 
                          {formatYesNo(plotData.Prad, "Prad")} 
                        </strong> 
                        {isYes(plotData.Prad, "Prad") && ( 
                          <> 
                            <p className="plot-legalization"> 
                              Rok legalizacji licznika:{" "} 
                              {plotData.Rok_leg_prad != null && 
                              String(plotData.Rok_leg_prad).trim() !== "" 
                                ? plotData.Rok_leg_prad 
                                : "Brak danych"} 
                            </p> 
                            {isYes(plotData.OczekujePrad) && ( 
                              <p className="plot-meter-warning"> 
                                Licznik oczekuje na wymianę 
                              </p> 
                            )} 
                          </> 
                        )} 
                      </div> 

                    {/* Woda */} 
                    <div className="plot-utility-card"> 
                      <h3>Woda</h3> 
                      <strong className={`plot-utility-status ${ 
                        isYes(plotData.Woda) 
                        ? "is-yes" 
                        : formatYesNo(plotData.Woda, "Woda") === "NIE" 
                        ? "is-no" : "is-yes" 
                        }`} 
                        > 
                        {formatYesNo(plotData.Woda, "Woda")} 
                      </strong> 
                      {isYes(plotData.Woda, "Woda") && (
                        <p className="plot-legalization"> 
                          Rok legalizacji wodomierza:{" "} 
                          {plotData.Rok_leg_woda != null && 
                          String(plotData.Rok_leg_woda).trim() !== "" 
                          ? plotData.Rok_leg_woda 
                          : "Brak danych"} 
                        </p> 
                      )} 
                    </div> 
                  </div>
                  {<Wiadomosci />}

                  </>
                )}
              </div>
            )}
              


            {/* DANE KONTA */}
            {activeTab === "konto" && (
              <>
                <div className="panel-heading">
                  <div>
                    <span className="panel-kicker">INFORMACJE</span>
                    <h2>Dane konta</h2>
                  </div>
                  <span className="panel-symbol">▤</span>
                </div>

                <div className="account-highlight">
                  <div className="account-avatar">
                    {user.Imie
                      ? user.Imie.charAt(0).toUpperCase()
                      : "U"}
                  </div>

                  <div>
                    <span className="field-label">Użytkownik</span>
                    <h3>{fullName || "Brak danych użytkownika"}</h3>
                    <p>{user.email || "Brak adresu e-mail"}</p>
                  </div>
                  <div className='investment-balance'>
                    <strong className={`plot-utility-status ${ 
                      isYes(user.Czlonek) 
                      ? "is-yes" 
                      : formatYesNo(user.Czlonek, "Czlonek") === "NIE" 
                      ? "is-no" : "is-yes" 
                      }`} 
                      > 
                      {formatYesNo(user.Czlonek, "Czlonek")} 
                    </strong> 
                  </div>
                </div>

                <div className="details-grid">
                  <div className="detail-card">
                    <span className="field-label">Nazwa ROD</span>
                    <strong>{user.Nazwa_1 || "Brak danych"}</strong>
                  </div>

                  <div className="detail-card">
                    <span className="field-label">Numer klienta</span>
                    <strong className="plot-value"> 
                          {plotData.NrKlienta != null && 
                          String(plotData.NrKlienta).trim() !== "" 
                          ? ` ${String(plotData.NrKlienta).trim()}`: "Brak danych"} 
                        </strong> 
                  </div>

                  <div className="detail-card">
                    <span className="field-label">
                      Data wstąpienia do PZD
                    </span>
                    <strong>{user.Data_wst || "Brak danych"}</strong>
                  </div>

                  <div className="detail-card">
                    <span className="field-label">
                      Data utworzenia konta
                    </span>
                    <strong>{user.created_at || "Brak danych"}</strong>
                  </div>

                  <div className="detail-card">
                    <span className="field-label">
                      
                    </span>
                    <strong>
                      
                    </strong>
                  </div>
                </div>

                <div className="account-extra-section">
                  <div className="panel-heading">
                    <div>
                      <span className="panel-kicker">
                        DANE KONTAKTOWE
                      </span>
                      <h2>Dane adresowe</h2>
                    </div>
                    <span className="panel-symbol">⌂</span>
                  </div>

                  <div className="details-grid">
                    <div className="detail-card">
                      <span className="field-label">Miasto</span>
                      <strong>{user.Miasto || "Brak danych"}</strong>
                    </div>

                    <div className="detail-card">
                      <span className="field-label">Kod pocztowy</span>
                      <strong>{user.Kod || "Brak danych"}</strong>
                    </div>

                    <div className="detail-card">
                      <span className="field-label">Adres</span>
                      <strong>
                        {[
                          user.Ulica,
                          user.Nr_dom,
                          user.Indeks_d,
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        {user.Nr_m != null && user.Nr_m !== ""
                          ? `/${user.Nr_m}${
                              user.Indeks_m
                                ? " " + user.Indeks_m
                                : ""
                            }`
                          : ""}
                      </strong>
                    </div>
                  </div>

                  <div className="panel-heading contact-heading">
                    <div>
                      <span className="panel-kicker">KONTAKT</span>
                      <h2>Telefon i e-mail</h2>
                    </div>
                    <span className="panel-symbol">✉</span>
                  </div>

                  <div className="details-grid">
                    <div className="detail-card">
                      <span className="field-label">Telefon</span>
                      <strong>{user.Tel_1 || "Brak danych"}</strong>
                    </div>

                    <div className="detail-card">
                      <span className="field-label">Adres e-mail</span>
                      <strong>{user.Email || "Brak danych"}</strong>
                    </div>
                  </div>

                  <div className="account-extra-section">
                    <div className="panel-heading contact-heading">
                      <div>
                        <span className="panel-kicker">
                          AKTUALIZACJA
                        </span>
                        <h2>Zgłoszenie zmiany danych</h2>
                      </div>
                      <span className="panel-symbol">✎</span>
                    </div>

                    <p>
                      Wypełnij tylko te pola, które chcesz zmienić.
                      Administrator zweryfikuje zgłoszenie.
                    </p>

                    <form
                      onSubmit={handleChangeSubmit}
                      className="account-change-form"
                    >
                      <div className="details-grid">
                        <label className="detail-card">
                          Imię
                          <input
                            value={changeForm.Imie}
                            onChange={(e) =>
                              setChangeForm({
                                ...changeForm,
                                Imie: e.target.value,
                              })
                            }
                            autoComplete="given-name"
                          />
                        </label>

                        <label className="detail-card">
                          Nazwisko
                          <input
                            value={changeForm.Nazwisko}
                            onChange={(e) =>
                              setChangeForm({
                                ...changeForm,
                                Nazwisko: e.target.value,
                              })
                            }
                            autoComplete="family-name"
                          />
                        </label>

                        <label className="detail-card">
                          Miasto
                          <input
                            value={changeForm.Miasto}
                            onChange={(e) =>
                              setChangeForm({
                                ...changeForm,
                                Miasto: e.target.value,
                              })
                            }
                            autoComplete="address-level2"
                          />
                        </label>

                        <label className="detail-card">
                          Kod pocztowy
                          <input
                            value={changeForm.Kod}
                            onChange={(e) =>
                              setChangeForm({
                                ...changeForm,
                                Kod: e.target.value,
                              })
                            }
                            autoComplete="postal-code"
                          />
                        </label>

                        <label className="detail-card">
                          Ulica
                          <input
                            value={changeForm.Ulica}
                            onChange={(e) =>
                              setChangeForm({
                                ...changeForm,
                                Ulica: e.target.value,
                              })
                            }
                            autoComplete="address-line1"
                          />
                        </label>

                        <label className="detail-card">
                          Numer domu
                          <input
                            value={changeForm.Nr_dom}
                            onChange={(e) =>
                              setChangeForm({
                                ...changeForm,
                                Nr_dom: e.target.value,
                              })
                            }
                          />
                        </label>

                        <label className="detail-card">
                          Numer mieszkania
                          <input
                            value={changeForm.Nr_m}
                            onChange={(e) =>
                              setChangeForm({
                                ...changeForm,
                                Nr_m: e.target.value,
                              })
                            }
                          />
                        </label>

                        <label className="detail-card">
                          Telefon
                          <input
                            type="tel"
                            value={changeForm.Tel_1}
                            onChange={(e) =>
                              setChangeForm({
                                ...changeForm,
                                Tel_1: e.target.value,
                              })
                            }
                            autoComplete="tel"
                          />
                        </label>

                        <label className="detail-card">
                          Adres e-mail
                          <input
                            type="email"
                            value={changeForm.Email}
                            onChange={(e) =>
                              setChangeForm({
                                ...changeForm,
                                Email: e.target.value,
                              })
                            }
                            autoComplete="email"
                          />
                        </label>
                      </div>

                      <label className="change-description">
                        Dodatkowe informacje
                        <textarea
                          value={changeForm.opis}
                          onChange={(e) =>
                            setChangeForm({
                              ...changeForm,
                              opis: e.target.value,
                            })
                          }
                          placeholder="Opisz, czego dotyczy zmiana..."
                          rows={4}
                        />
                      </label>

                      {changeMessage && (
                        <p className="app-message" role="status">
                          {changeMessage}
                        </p>
                      )}

                      {changeError && (
                        <p className="app-error" role="alert">
                          {changeError}
                        </p>
                      )}

                      <button
                        type="submit"
                        className="logout-button"
                        disabled={changeLoading}
                      >
                        {changeLoading
                          ? "Wysyłanie..."
                          : "Wyślij zgłoszenie"}
                      </button>
                    </form>
                  </div>
                </div>
              </>
            )}

            {/* FINANSE */}
            {activeTab === "finanse" && (
              <div className="content-section">
                <h2>Finanse</h2>

                <div className="finance-tabs">
                  <button
                    type="button"
                    className={
                      financeTab === "podstawowe"
                        ? "finance-tab active"
                        : "finance-tab"
                    }
                    onClick={() => setFinanceTab("podstawowe")}
                  >
                    Konto podstawowe
                  </button>

                  <button
                    type="button"
                    className={
                      financeTab === "inwestycyjne"
                        ? "finance-tab active"
                        : "finance-tab"
                    }
                    onClick={() => setFinanceTab("inwestycyjne")}
                  >
                    Konto inwestycyjne
                  </button>
                  <div className="investment-balance">
                    Nr konta:{" "}
                    
                    <strong>
                      {financeTab === "podstawowe"
                        ? nrKontoPodstawowe || "Brak numeru konta"
                        : nrKontoInwestycyjne || "Brak numeru konta"}
                    </strong>    
                  </div>
                </div>

                <div className="finance-panel">
                  {financeTab === "podstawowe" && (
                    <div>
                      <div className="investment-heading">
                        <h3>Konto podstawowe</h3>
                        <div className="investment-balance">
                          Saldo:{" "}
                          <strong>
                            {basicAccountBalance.toLocaleString(
                              "pl-PL",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )}
                          </strong>
                        </div>
                      </div>

                      <div className="investment-legend">
                        <span className="investment-legend-w">
                          ● Należności (data wymagalności)
                        </span>
                        <span className="investment-legend-m">
                          ● Wpłaty (data wpływu)
                        </span>
                      </div>

                      {basicAccountReadings.length === 0 ? (
                        <p>
                          Brak operacji na koncie podstawowym od daty
                          wstąpienia.
                        </p>
                      ) : (
                        <div className="table-container">
                          <table className="readings-table investment-table">
                            <thead>
                              <tr>
                                <th>Data</th>
                                <th>Rodzaj operacji</th>
                                <th>Kwota</th>
                              </tr>
                            </thead>

                            <tbody>
                              {basicAccountReadings.map(
                                (operation, index) => (
                                  <tr
                                    key={`${operation.typ}-${operation.Data}-${index}`}
                                    className={
                                      operation.typ === "W"
                                        ? "investment-row-w"
                                        : "investment-row-m"
                                    }
                                  >
                                    <td>{operation.Data}</td>
                                    <td>{operation.opis}</td>
                                    <td>
                                      {Number(
                                        operation.Kwota
                                      ).toLocaleString("pl-PL", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </td>
                                  </tr>
                                )
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {financeTab === "inwestycyjne" && (
                    <div>
                      <div className="investment-heading">
                        <h3>Konto inwestycyjne</h3>
                        <div className="investment-balance">
                          Saldo:{" "}
                          <strong>
                            {investmentBalance.toLocaleString(
                              "pl-PL",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )}
                          </strong>
                        </div>
                      </div>

                      <div className="investment-legend">
                        <span className="investment-legend-w">
                          ● Należności (data wymagalności)
                        </span>
                        <span className="investment-legend-m">
                          ● Wpłaty (data wpływu)
                        </span>
                      </div>

                      {investmentReadings.length === 0 ? (
                        <p>
                          Brak operacji na koncie inwestycyjnym od daty
                          wstąpienia.
                        </p>
                      ) : (
                        <div className="table-container">
                          <table className="readings-table investment-table">
                            <thead>
                              <tr>
                                <th>Data</th>
                                <th>Kwota</th>
                              </tr>
                            </thead>

                            <tbody>
                              {investmentReadings.map(
                                (operation, index) => (
                                  <tr
                                    key={`${operation.typ}-${operation.Data}-${index}`}
                                    className={
                                      operation.typ === "W"
                                        ? "investment-row-w"
                                        : "investment-row-m"
                                    }
                                  >
                                    <td>{operation.Data}</td>
                                    <td>
                                      {Number(
                                        operation.Kwota
                                      ).toLocaleString("pl-PL", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </td>
                                  </tr>
                                )
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* LICZNIK WODY */}
            {activeTab === "woda" && (
              <div className="content-section">
                <div className="water-readings">
                  <h2>💧 Odczyty licznika wody</h2>

                  {waterReadings.length === 0 ? (
                    <p>
                      Brak odczytów wody od daty wstąpienia.
                    </p>
                  ) : (
                    <div className="table-container">
                      <table className="readings-table">
                        <thead>
                          <tr>
                            <th>Rok</th>
                            <th>Data odczytu</th>
                            <th>Stan</th>
                            <th>Nr licznika</th>
                          </tr>
                        </thead>

                        <tbody>
                          {waterReadings.map((odczyt, index) => (
                            <tr
                              key={`${odczyt.Nr_licznik}-${odczyt.Data_odczyt}-${index}`}
                            >
                              <td>{odczyt.Rok}</td>
                              <td>{odczyt.Data_odczyt}</td>
                              <td>{odczyt.Stan}</td>
                              <td>{odczyt.Nr_licznik}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* LICZNIK PRĄDU */}
            {activeTab === "prad" && (
              <div className="content-section">
                <h2>⚡ Odczyty licznika prądu</h2>

                {powerReadings.length === 0 ? (
                  <p>
                    Brak odczytów prądu od daty wstąpienia.
                  </p>
                ) : (
                  <div className="table-container">
                    <table className="readings-table">
                      <thead>
                        <tr>
                          <th>Rok</th>
                          <th>Data odczytu</th>
                          <th>Stan</th>
                          <th>Nr licznika</th>
                        </tr>
                      </thead>

                      <tbody>
                        {powerReadings.map((odczyt, index) => (
                          <tr
                            key={`${odczyt.Nr_licznik}-${odczyt.Data_odczyt}-${index}`}
                          >
                            <td>{odczyt.Rok}</td>
                            <td>{odczyt.Data_odczyt}</td>
                            <td>{odczyt.Stan}</td>
                            <td>{odczyt.Nr_licznik}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          <footer className="page-footer">
            <span>Panel klienta</span>
            <span>Bezpieczny dostęp do Twoich danych</span>
          </footer>
        </section>
      </main>
    </div>
  );
}

export default App;